import FileDownload from '@mui/icons-material/FileDownload';
import { FunctionComponent } from 'react';
import { Trans } from 'react-i18next';

import useElasticSearch from '../../hooks/useElasticSearch';
import ExinMuiCircularProgress from '../styled/ExinMuiCircularProgress';
import ExinMuiIconButton from '../styled/ExinMuiIconButton';
import { ExinSmallText } from '../styled/ExinText';
import { Certification } from './StepCertification';
import { Language } from './StepCertificationLanguage';

type Download = {
  name: string;
  url: string;
};

function transformServerResponse(garbage: any): Array<Download> {
  return garbage.hits.hits.reduce((all: Array<Download>, hit: any) => {
    const materialsCount = parseInt(hit._source.meta?.materials[0].raw, 10) || 0;
    for (let i = 0; i < materialsCount; i++) {
      if (
        !hit._source.meta?.[`materials_${i}_title`] ||
        !hit._source.meta?.[`materials_${i}_url`]
      ) {
        continue;
      }
      all.push({
        name: hit._source.meta[`materials_${i}_title`][0].value,
        url: hit._source.meta[`materials_${i}_url`][0].value,
      });
    }
    return all;
  }, []);
}

const StepDownload: FunctionComponent<{
  certification: Certification;
  language: Language;
}> = ({ certification, language }) => {
  const { isLoading, response: downloads } = useElasticSearch(
    {
      query: {
        bool: {
          must: [
            {
              term: {
                'post_type.raw': 'ex_certification',
              },
            },
            {
              term: {
                post_id: parseInt(certification.value, 10),
              },
            },
          ],
        },
      },
      size: 10000,
      // Downloads (or "materials") are stored on Certification objects a little awkwardly in ES.
      // Instead of them occurring in an array, each material has its own field, and for each
      // material three fields exist; `material_1_title`, `material_1_url`, `material_1_material_type`,
      // `material_1_language`, `material_2_title`, etc.
      _source: Array.from(new Array(certification.materialsCount)).reduce(
        (source, _, index) => [
          ...source,
          `meta.materials_${index}_title.value`,
          `meta.materials_${index}_url.value`,
        ],
        ['post_id', 'meta.materials.raw']
      ),
    },
    transformServerResponse
  );

  if (isLoading) {
    return <ExinMuiCircularProgress />;
  }
  if (!downloads?.length) {
    return <Trans i18nKey="messageNoDownloads">No downloads found</Trans>;
  }

  return (
    <div>
      {downloads.map((download, i) => (
        <ExinSmallText key={i}>
          <a href={download.url}>
            <ExinMuiIconButton>
              <FileDownload />
            </ExinMuiIconButton>
            {download.name}
          </a>
        </ExinSmallText>
      ))}
    </div>
  );
};

export default StepDownload;
